import React, { useState, useEffect } from "react";
import axios from "axios";
import styled from "@emotion/styled";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { DatePicker } from "react-dayjs-picker";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";

// layout
import Layout from "layout/Layout";
import SubLayout from "layout/SubLayout";

// components
import Input from "components/Input/Input";
import Button from "components/Button/Button";
import FileInput from "components/FileInput/FileInput";
import Row from "containers/LabelWithInput/LabelWithInput";

// store
import { isMobileWidthAtom } from "store/ui/atom";

// hooks
import useAlert from "hooks/useAlert";

// utils
import { PAGE_LAYOUT, PAGE_LAYOUT_EN } from "utils/pageLayoutConstants";
import promotionValidationSchema from "utils/validation/promotionValidationSchema";
import promotionValidationSchemaEn from "utils/validation/promotionValidationSchemaEn";

// assets
import Banner05 from "assets/images/pc/img_banner05.png";
import IconCalSVG from "assets/images/pc/icon_cal.svg";

import "react-dayjs-picker/dist/index.css";

const defaultValues = {
  PromotionTitle: "",
  PromotionRegDtm: "",
  PromotionDeadline: "",
  PromotionSite: "",
  PromotionImage: "",
  PromotionContent: "",
};

/**
 * @file 커리어 - 행사공지 등록 페이지
 * @brief 행사공지 등록 Form Validation
 * @author TW.Lee
 */
const PromotionRegist = () => {
  const { noti } = useAlert();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [regDtmOpen, setRegDtmOpen] = useState(false);
  const [deadlineOpen, setDeadlineOpen] = useState(false);
  const [submitState, setSubmitState] = useState("schemako");
  const isMobileWidth = useRecoilValue(isMobileWidthAtom);

  const UI = {
    schemako: promotionValidationSchema,
    schemaen: promotionValidationSchemaEn,
  };

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues,
    shouldFocusError: true,
    resolver: yupResolver(UI[submitState]),
    mode: "onChange",
    reValidateMode: "onChange",
  });
  watch();
  const values = getValues();

  useEffect(() => {
    if (t("lang") === "EN") {
      setSubmitState("schemako");
    } else {
      setSubmitState("schemaen");
    }
  }, [t("lang")]);

  useEffect(() => {
    setValue("PromotionSite", "https://");
  }, []);

  useEffect(() => {
    if (values.PromotionImage[0]?.size > 1024 * 1024 * 5) {
      // 용량 초과시 경고후 해당 파일의 용량도 보여줌
      if (t("lang") === "EN") {
        noti("업로드 할 수 있는 파일 형식과 최대 용량을 확인해주세요.");
      } else {
        noti(
          "Please check the file format and maximum capacity that can be uploaded.",
          "Confirm"
        );
      }
      setValue("PromotionImage", "");
    }

    const reg = /jpg|jpeg|png/i;
    if (values.PromotionImage[0] !== undefined) {
      if (
        reg.test(
          values.PromotionImage[0]?.name.substring(
            values.PromotionImage[0]?.name.lastIndexOf(".") + 1
          )
        ) === false
      ) {
        if (t("lang") === "EN") {
          noti("업로드 할 수 있는 파일 형식과 최대 용량을 확인해주세요.");
        } else {
          noti(
            "Please check the file format and maximum capacity that can be uploaded.",
            "Confirm"
          );
        }
        setValue("PromotionImage", "");
      }
    }
  }, [values.PromotionImage]);

  useEffect(() => {
    if (values.PromotionRegDtm > values.PromotionDeadline) {
      if (t("lang") === "EN") {
        noti("마감일은 시작일보다 빠른 날짜를 선택할 수 없습니다.");
      } else {
        noti("The End date cannot be earlier than the start date.");
      }
      setValue("PromotionDeadline", "");
    }
  }, [values.PromotionDeadline]);

  const imageFormData = new FormData();
  imageFormData.append("file", values.PromotionImage[0]);

  const addEventPost = async (imageId) => {
    await axios({
      method: "post",
      url: "/posts",
      data: {
        type: "EVENT",
        title: values.PromotionTitle,
        content: values.PromotionContent,
        start_date: values.PromotionRegDtm,
        end_date: values.PromotionDeadline,
        web_site: values.PromotionSite,
        image: imageId,
      },
    }).then((res) => {
      if (res.status === 200) {
        if (t("lang") === "EN") {
          noti("등록되었습니다.");
        } else {
          noti("Registered.", "Confirm");
        }
        navigate("/promotion", { replace: true });
      }
    });
  };

  const addFile = async () => {
    await axios({
      method: "post",
      url: "/files",
      data: imageFormData,
    }).then((res) => {
      if (res.status === 200) {
        addEventPost(res.data.result.id);
      }
    });
  };

  return (
    <Layout subFixed="true">
      <WhiteRoot>
        <SubLayout
          banner={Banner05}
          pageText={
            t("lang") === "EN"
              ? PAGE_LAYOUT.promotionregist
              : PAGE_LAYOUT_EN.promotionregist
          }
        >
          <form
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();

              handleSubmit(
                () => {
                  if (values.PromotionImage[0]) {
                    addFile();
                  } else {
                    addEventPost();
                  }
                },
                (err) => {
                  const keys = [
                    "PromotionTitle",
                    "PromotionRegDtm",
                    "PromotionDeadline",
                    "PromotionSite",
                    "PromotionContent",
                  ];

                  for (const key of keys) {
                    if (err[key]) {
                      if (t("lang") === "EN") {
                        noti(err[key].message, "확인");
                      } else {
                        noti(err[key].message, "Confirm");
                      }
                      break;
                    }
                  }
                }
              )();
            }}
          >
            <PromotionRegistRegistInner>
              <div
                className={isMobileWidth ? "mo_promotion-box" : "promotion-box"}
              >
                <Row label={t("title")}>
                  <div className={isMobileWidth ? "mo_input-box" : "input-box"}>
                    <Controller
                      control={control}
                      name="PromotionTitle"
                      render={({ field }) => (
                        <Input
                          {...field}
                          className="inp-item"
                          type="text"
                          placeholder={t("hiringregisttext1")}
                          maxLength="50"
                          fullWidth
                        />
                      )}
                    />
                  </div>
                </Row>
                <Row label={t("period")}>
                  <div
                    className={
                      isMobileWidth
                        ? "mo_input-box date-section"
                        : "input-box date-section"
                    }
                  >
                    <div
                      className={isMobileWidth ? "mo-date-input" : "date-input"}
                    >
                      <div
                        className={
                          isMobileWidth
                            ? "mo-date-image mo-date-image1"
                            : "date-image date-image1"
                        }
                        onClick={() => {
                          setRegDtmOpen(true);
                        }}
                      >
                        <img src={IconCalSVG} alt="캘린더" />
                      </div>
                      <Controller
                        control={control}
                        name="PromotionRegDtm"
                        render={({ field }) => (
                          <DatePicker
                            register={register("PromotionRegDtm")}
                            isOpen={regDtmOpen}
                            setIsOpen={setRegDtmOpen}
                            popoverPositions={["bottom"]}
                            onSelect={(date) => {
                              field.onChange(dayjs(date).format("YYYY-MM-DD"));
                            }}
                            value={field.value}
                            closeOnSelect={true}
                            placeholder={t("startdate")}
                            onChange={() => {}}
                          />
                        )}
                      />
                    </div>
                    <span>-</span>
                    <div
                      className={isMobileWidth ? "mo-date-input" : "date-input"}
                    >
                      <div
                        className={
                          isMobileWidth
                            ? "mo-date-image mo-date-image2"
                            : "date-image date-image2"
                        }
                        onClick={() => {
                          setDeadlineOpen(true);
                        }}
                      >
                        <img src={IconCalSVG} alt="캘린더" />
                      </div>
                      <Controller
                        control={control}
                        name="PromotionDeadline"
                        render={({ field }) => (
                          <DatePicker
                            register={register("PromotionDeadline")}
                            isOpen={deadlineOpen}
                            setIsOpen={setDeadlineOpen}
                            popoverPositions={["bottom"]}
                            onSelect={(date) => {
                              field.onChange(dayjs(date).format("YYYY-MM-DD"));
                            }}
                            value={field.value}
                            closeOnSelect={true}
                            placeholder={t("enddate")}
                            onChange={() => {}}
                          />
                        )}
                      />
                    </div>
                  </div>
                </Row>
                <Row label={t("link")}>
                  <div className={isMobileWidth ? "mo_input-box" : "input-box"}>
                    <Controller
                      control={control}
                      name="PromotionSite"
                      render={({ field }) => (
                        <Input
                          {...field}
                          className="inp-item"
                          type="text"
                          placeholder="https://"
                          fullWidth
                        />
                      )}
                    />
                  </div>
                </Row>
                <Row label={t("image")}>
                  <div className={isMobileWidth ? "mo_input-box" : "input-box"}>
                    <div className="attachment">
                      <Controller
                        control={control}
                        name="PromotionImage"
                        render={({ field }) => (
                          <FileInput
                            {...field}
                            accept=".jpg, .png"
                            className="inp-item"
                          >
                            {t("hiringregisttext3")}
                          </FileInput>
                        )}
                      />
                    </div>
                  </div>
                </Row>
                <Row label={t("hiringcontent")}>
                  <div className={isMobileWidth ? "mo_input-box" : "input-box"}>
                    <Controller
                      control={control}
                      name="PromotionContent"
                      render={({ field }) => (
                        <Input
                          {...field}
                          multiline
                          useMaxLengthIndicator
                          maxLength={1000}
                          className="inp-item"
                          type="text"
                          placeholder={t("hiringregisttext4")}
                          doubleheight={isMobileWidth ? "" : "true"}
                          fullWidth
                        />
                      )}
                    />
                  </div>
                </Row>
              </div>
            </PromotionRegistRegistInner>
            <div className="submit-button">
              <Button type="submit" variant="primary" preset="1">
                {t("regist")}
              </Button>
            </div>
          </form>
        </SubLayout>
      </WhiteRoot>
    </Layout>
  );
};

export default PromotionRegist;

const WhiteRoot = styled.div`
  & > div > div > .float_wrap > div {
    padding: 0 410px;
  }

  & .submit-button {
    display: flex;
    justify-content: center;
    padding-bottom: 100px;
  }
`;

const PromotionRegistRegistInner = styled.div`
  & .promotion-box {
    margin: 60px 0;
    padding: 40px 0;
    border-top: 1px solid #222;
    border-bottom: 1px solid #ddd;
  }
  & .mo_promotion-box {
    margin: 40px 0;
    padding: 20px 0;
    border-top: 1px solid #222;
    border-bottom: 1px solid #ddd;
  }

  & .input-box {
    margin: 16px 0 30px;
  }
  & .mo_input-box {
    margin: 10px 0 20px;
  }

  & .date-section {
    width: 100%;
    display: flex;
    align-items: center;

    & span {
      margin: 0 10px;
    }
  }

  & .date-image {
    position: absolute;
    margin-top: 8px;
    right: 31%;
    transform: translateX(96%);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 49px;
    height: 49px;
    background-color: #444;
    cursor: pointer;
  }
  & .date-image1 {
    margin-top: 8px;
    right: 54%;
    transform: translateX(78%);
  }
  & .date-image2 {
    margin-top: 8px;
    right: 31%;
    transform: translateX(96%);
  }

  & .mo-date-image {
    position: absolute;
    margin-top: 4px;
    transform: translateX(96%);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    background-color: #444;
    cursor: pointer;
  }
  & .mo-date-image1 {
    margin-top: 4px;
    right: 50%;
    transform: translateX(-50%);
  }
  & .mo-date-image2 {
    margin-top: 4px;
    right: 63px;
    transform: translateX(96%);
  }

  & .date-input {
    width: 100%;

    input::placeholder {
      color: #b2b2b2;
    }
    input[name="rdp-date-picker"] {
      width: 100%;
      height: 65px;
      border: solid 1px #ddd;
      background-color: #fff;
      line-height: 1.29;
      font-size: 16px;
      color: #000;
      padding-left: 20px;
    }
    .react-tiny-popover-container {
      height: 100px;
      background-color: red;
    }
  }

  & .mo-date-input {
    width: 100%;

    input::placeholder {
      color: #b2b2b2;
    }
    input[name="rdp-date-picker"] {
      width: 100%;
      height: 48px;
      border: solid 1px #ddd;
      background-color: #fff;
      line-height: 1.29;
      font-size: 14px;
      color: #000;
      padding-left: 20px;
    }
    .react-tiny-popover-container {
      height: 100px;
      background-color: red;
    }
  }
`;
